<template>
  <div>
    <!-- Clever Configuration Display -->
    <div v-if="rosterService === ROSTER_SERVICE_CLEVER" class="form-group">
        <p>
          Bearer Token: <code v-if="showSecret">{{ cleverConfig.bearerToken }}</code>
          <code v-else-if="!showSecret && cleverConfig.bearerToken">••••••••</code>
          <span v-else><strong>Not set</strong></span>
          <button
            v-if="cleverConfig.bearerToken"
            type="button" 
            class="btn btn-link btn-sm" 
            @click="toggleSecret"
            :aria-label="showSecret ? 'Hide bearer token' : 'Show bearer token'"
            :aria-pressed="showSecret">
            {{ showSecret ? '(hide)' : '(show)' }}
          </button>
        </p>
        <div>
          <p>District Admins: <strong>{{ cleverConfig.districtAdmins || 'Not set' }}</strong></p>
          <ul v-if="cleverConfig.districtAdminEmails && cleverConfig.districtAdminEmails.length > 0">
            <li v-for="(email, index) in cleverConfig.districtAdminEmails" :key="index">
              {{ email }}
            </li>
          </ul>
        </div>
      </div>

      <!-- OneRoster Configuration Display -->
      <div v-if="rosterService === ROSTER_SERVICE_ONEROSTER" class="form-group">
        <p>Client ID: <strong>{{ oneRosterConfig.clientId || 'Not set' }}</strong></p>
        <p>
          Client Secret: <code v-if="showSecret">{{ oneRosterConfig.clientSecret }}</code>
          <code v-else-if="!showSecret && oneRosterConfig.clientSecret">••••••••</code>
          <span v-else><strong>Not set</strong></span>
          <button
            v-if="oneRosterConfig.clientSecret"
            type="button" 
            class="btn btn-link btn-sm" 
            @click="toggleSecret"
            :aria-label="showSecret ? 'Hide client secret' : 'Show client secret'"
            :aria-pressed="showSecret">
            {{ showSecret ? '(hide)' : '(show)' }}
          </button>
        </p>
        <p>Endpoint URL: <strong>{{ oneRosterConfig.endpoint || 'Not set' }}</strong></p>
        <p>API Version: <strong>{{ oneRosterConfig.apiVersion || 'Not set' }}</strong></p>
        <p>Unique ID Field: <strong>{{ oneRosterConfig.uniqueIdField || 'Not set' }}</strong></p>
        <p v-if="oneRosterConfig.titleField">Title Field: <strong>{{ oneRosterConfig.titleField || 'Not set' }}</strong></p>
      </div>

      <!-- S3 CSV Configuration Display -->
      <div v-if="rosterService === ROSTER_SERVICE_S3CSV" class="form-group">
        <p>S3 CSV Input Directory: <code>{{ s3CsvConfig.inputDirectory || 'Not set' }}</code></p>
        <p v-if="s3CsvConfig.filenamePrefix">File Name Prefix: <code>{{ s3CsvConfig.filenamePrefix}}</code></p>
        <div v-if="Object.values(s3CsvConfig.fieldMappings).some(value => value)">
          <p>Custom Field Mappings:</p>
          <ul>
            <li>Name Field: <strong>{{ s3CsvConfig.fieldMappings.name_field }}</strong></li>
            <li>Email Field: <strong>{{ s3CsvConfig.fieldMappings.email_field }}</strong></li>
            <li>Title Field: <strong>{{ s3CsvConfig.fieldMappings.title_field }}</strong></li>
            <li>School Field: <strong>{{ s3CsvConfig.fieldMappings.school_field }}</strong></li>
            <li v-if="s3CsvConfig.fieldMappings.external_id_field">External ID Field: <strong>{{ s3CsvConfig.fieldMappings.external_id_field }}</strong></li>
          </ul>
        </div>
        
        <!-- Latest File Information -->
        <div class="latest-file-info">
          <p class="control-label">Latest File Information:</p>
          <div v-if="loadingFileData" class="text-muted">
            <i class="fa fa-spinner fa-spin"></i> Loading file information...
          </div>
          <div v-else-if="fileDataError" class="text-danger">
            <i class="fa fa-exclamation-triangle"></i> {{ fileDataError }}
          </div>
          <div v-else-if="latestFileData">
            <p>File Name: <code>{{ latestFileData.file_name }}</code><file-download-button v-if="latestFileData.download_url" :url="latestFileData.download_url" :file-name="latestFileData.file_name" /></p>
            <p class="last-modified-row">
              <span>Last Modified: <strong>{{ formattedLastModified }}</strong></span>
              <stale-file-icon v-if="isStaleFile" />
            </p>
            <p>File Size: <strong>{{ formattedFileSize }}</strong></p>
          </div>
          <div v-else class="text-muted">
            No files found
          </div>
        </div>
      </div>
  </div>
</template>

<script>
import { ROSTER_SERVICE_CLEVER, ROSTER_SERVICE_ONEROSTER, ROSTER_SERVICE_S3CSV } from './constants'
import { getCleverConfigDisplay, getOneRosterConfigDisplay, getS3CsvConfigDisplay } from './utils/displayHelpers'
import { Tooltip } from 'uiv'
import { formatDate, formatFileSize, isOlderThanDays } from '../../utils/formatters'
import FileDownloadButton from './FileDownloadButton.vue'
import StaleFileIcon from './StaleFileIcon.vue'

export default {
  name: 'AutoprovisioningConfigDisplay',
  components: { FileDownloadButton, StaleFileIcon, Tooltip },
  props: {
    config: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      ROSTER_SERVICE_CLEVER,
      ROSTER_SERVICE_ONEROSTER,
      ROSTER_SERVICE_S3CSV,
      showSecret: false,
      latestFileData: null,
      loadingFileData: false,
      fileDataError: null
    }
  },
  computed: {
    rosterService() {
      return this.config?.lambda_input?.roster_service || ''
    },
    cleverConfig() {
      return getCleverConfigDisplay(this.config?.lambda_input) 
    },
    oneRosterConfig() {
      return getOneRosterConfigDisplay(this.config?.lambda_input)
    },
    s3CsvConfig() {
      return getS3CsvConfigDisplay(this.config?.lambda_input)
    },
    formattedLastModified() {
      if (this.latestFileData == null || this.latestFileData.last_modified == null) {
        return 'N/A'
      }
      return formatDate(new Date(this.latestFileData.last_modified))
    },
    isStaleFile() {
      return isOlderThanDays(this.latestFileData?.last_modified)
    },
    formattedFileSize() {
      if (this.latestFileData == null || this.latestFileData.file_size == null) {
        return 'N/A'
      }
      return formatFileSize(this.latestFileData.file_size)
    }
  },
  methods: {
    toggleSecret() {
      this.showSecret = !this.showSecret
    },
    fetchLatestFileData() {
      if (this.rosterService !== ROSTER_SERVICE_S3CSV) {
        return
      }
      
      if (!this.$angularWorld || !this.$angularWorld.adminApi || !this.config?.domain_uuid) {
        return
      }
      
      this.loadingFileData = true
      this.fileDataError = null
      
      const domainUuid = this.config.domain_uuid
      
      this.$angularWorld.adminApi.getLatestCSVMetadata(domainUuid)
        .then(data => {
          this.latestFileData = data
          this.loadingFileData = false
        })
        .catch(error => {
          console.error('Error fetching latest CSV data:', error)
          this.fileDataError = 'Unable to retrieve file information'
          this.loadingFileData = false
        })
    }
  },
  watch: {
    rosterService() {
      this.showSecret = false
    }
  },
  mounted() {
    this.fetchLatestFileData()
  }
}
</script>
<style scoped>
.control-label {
  font-weight: bold;
  margin-bottom: 0;
}

.last-modified-row {
  display: flex;
  align-items: center;
  gap: 4px;
}

</style>
